import { useState, useEffect, useMemo } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { HelpPanel } from '../components/ui/help-panel';
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Pencil,
  Eye,
  EyeOff,
  Filter,
  BookOpen,
  Wallet,
  TrendingUp,
  TrendingDown,
  PiggyBank,
  Building2,
  ChevronRight,
  FileText,
  Copy,
  ToggleLeft,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { apiFetch } from '../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const accountTypes = [
  { value: 'asset', label: 'Asset', icon: Wallet, color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  { value: 'liability', label: 'Liability', icon: Building2, color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400' },
  { value: 'equity', label: 'Equity', icon: PiggyBank, color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
  { value: 'income', label: 'Income', icon: TrendingUp, color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' },
  { value: 'expense', label: 'Expense', icon: TrendingDown, color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
];

const normalSides = [
  { value: 'debit', label: 'Debit' },
  { value: 'credit', label: 'Credit' },
];

// Help content for COA
const coaHelp = {
  title: 'Chart of Accounts',
  description: 'Understanding your account structure',
  sections: [
    {
      heading: 'What is the Chart of Accounts?',
      content: 'Your chart of accounts is the foundation of your financial reporting. It\'s a categorized list of all accounts used to record transactions in your general ledger.'
    },
    {
      heading: 'Account Types',
      type: 'info',
      content: (
        <ul className="space-y-1 mt-2 text-sm">
          <li><strong>Assets</strong> (1xxx) - What you own: cash, equipment, receivables</li>
          <li><strong>Liabilities</strong> (2xxx) - What you owe: loans, payables, credit cards</li>
          <li><strong>Equity</strong> (3xxx) - Owner investment and retained earnings</li>
          <li><strong>Income</strong> (4xxx) - Revenue from sales and services</li>
          <li><strong>Expenses</strong> (5xxx-6xxx) - Costs of running the business</li>
        </ul>
      )
    },
    {
      heading: 'Best Practices',
      type: 'tip',
      content: 'Use consistent numbering (1000s for assets, 2000s for liabilities, etc.). Keep account names descriptive but concise.'
    }
  ],
  tips: [
    'Start with the default chart and customize as needed',
    'Use sub-accounts to track detail without cluttering reports',
    'Mark unused accounts as inactive rather than deleting them'
  ]
};

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, color }) => (
  <div className={cn(
    "flex items-center gap-3 px-4 py-3 rounded-lg border",
    color === 'blue' && "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900",
    color === 'rose' && "bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-900",
    color === 'purple' && "bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-900",
    color === 'emerald' && "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900",
    color === 'amber' && "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900"
  )}>
    <Icon className={cn(
      "w-5 h-5",
      color === 'blue' && "text-blue-600 dark:text-blue-400",
      color === 'rose' && "text-rose-600 dark:text-rose-400",
      color === 'purple' && "text-purple-600 dark:text-purple-400",
      color === 'emerald' && "text-emerald-600 dark:text-emerald-400",
      color === 'amber' && "text-amber-600 dark:text-amber-400"
    )} />
    <div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  </div>
);

export default function ChartOfAccounts() {
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'code', direction: 'asc' });
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    type: 'asset',
    normal_side: 'debit',
    is_control: false,
    control_kind: '',
  });

  // COA Migration state
  const [migrationOpen, setMigrationOpen] = useState(false);
  const [migrationStep, setMigrationStep] = useState('select'); // select | preview | apply
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [migrationPreview, setMigrationPreview] = useState(null);
  const [accountMappings, setAccountMappings] = useState({});
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState('general');

  const fetchAccounts = async () => {
    if (!currentWorkspace) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/accounts?active_only=${!showInactive}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setAccounts(data);
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [currentWorkspace, showInactive]);

  // Fetch available templates and current workspace template
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/coa-templates`);
        if (res.ok) {
          const data = await res.json();
          setTemplates(data.templates || []);
        }
      } catch (e) { console.error('Failed to fetch templates:', e); }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
    const fetchWsInfo = async () => {
      if (!currentWorkspace?.workspace_id) return;
      try {
        const res = await apiFetch(`${API_URL}/api/workspaces/${currentWorkspace.workspace_id}`);
        if (res.ok) {
          const data = await res.json();
          setCurrentTemplate(data.industry_template || 'general');
        }
      } catch (e) { console.error('Failed to fetch workspace info:', e); }
    };
    fetchWsInfo();
  }, [currentWorkspace?.workspace_id]);

  const handlePreviewMigration = async () => {
    if (!selectedTemplate) return;
    setMigrationLoading(true);
    try {
      const res = await apiFetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/coa-migration-preview?template_id=${selectedTemplate}`
      );
      if (res.ok) {
        const data = await res.json();
        setMigrationPreview(data);
        // Pre-populate mappings as empty (user chooses)
        const defaultMappings = {};
        (data.unmapped || []).forEach(u => { defaultMappings[u.account_id] = ''; });
        setAccountMappings(defaultMappings);
        setMigrationStep('preview');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to preview migration');
      }
    } catch (e) {
      toast.error('Failed to preview migration');
    } finally {
      setMigrationLoading(false);
    }
  };

  const handleApplyMigration = async () => {
    setMigrationLoading(true);
    try {
      // Build account_mappings: { old_account_id: new_code }
      const mappingsPayload = {};
      Object.entries(accountMappings).forEach(([acctId, newCode]) => {
        if (newCode && newCode !== '__none__') mappingsPayload[acctId] = newCode;
      });
      const res = await apiFetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/apply-coa-template`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            template_id: selectedTemplate,
            account_mappings: mappingsPayload
          })
        }
      );
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Template applied successfully');
        setMigrationOpen(false);
        resetMigration();
        setCurrentTemplate(selectedTemplate);
        fetchAccounts();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to apply template');
      }
    } catch (e) {
      toast.error('Failed to apply template');
    } finally {
      setMigrationLoading(false);
    }
  };

  const resetMigration = () => {
    setMigrationStep('select');
    setSelectedTemplate('');
    setMigrationPreview(null);
    setAccountMappings({});
  };

  // Calculate stats
  const stats = useMemo(() => {
    return {
      total: accounts.length,
      assets: accounts.filter(a => a.type === 'asset').length,
      liabilities: accounts.filter(a => a.type === 'liability').length,
      equity: accounts.filter(a => a.type === 'equity').length,
      income: accounts.filter(a => a.type === 'income').length,
      expenses: accounts.filter(a => a.type === 'expense').length,
    };
  }, [accounts]);

  const handleCreateAccount = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/accounts`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        }
      );

      if (response.ok) {
        toast.success('Account created successfully');
        setDialogOpen(false);
        resetForm();
        fetchAccounts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create account');
      }
    } catch (error) {
      console.error('Failed to create account:', error);
      toast.error('Failed to create account');
    }
  };

  const handleUpdateAccount = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/accounts/${editingAccount.account_id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formData.name,
            is_active: formData.is_active,
          }),
        }
      );

      if (response.ok) {
        toast.success('Account updated successfully');
        setDialogOpen(false);
        resetForm();
        fetchAccounts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update account');
      }
    } catch (error) {
      console.error('Failed to update account:', error);
      toast.error('Failed to update account');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      type: 'asset',
      normal_side: 'debit',
      is_control: false,
      control_kind: '',
    });
    setEditingAccount(null);
  };

  const openEditDialog = (account) => {
    setEditingAccount(account);
    setFormData({
      code: account.code,
      name: account.name,
      type: account.type,
      normal_side: account.normal_side,
      is_control: account.is_control,
      control_kind: account.control_kind || '',
      is_active: account.is_active,
    });
    setDialogOpen(true);
  };

  const toggleAccountStatus = async (account) => {
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/accounts/${account.account_id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_active: !account.is_active }),
        }
      );

      if (response.ok) {
        toast.success(`Account ${account.is_active ? 'deactivated' : 'activated'}`);
        fetchAccounts();
      }
    } catch (error) {
      console.error('Failed to toggle account status:', error);
      toast.error('Failed to update account');
    }
  };

  const filteredAccounts = useMemo(() => {
    let filtered = accounts.filter(account => {
      const matchesSearch = 
        account.code.toLowerCase().includes(search.toLowerCase()) ||
        account.name.toLowerCase().includes(search.toLowerCase());
      const matchesType = typeFilter === 'all' || account.type === typeFilter;
      return matchesSearch && matchesType;
    });
    
    // Sort
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle null/undefined
        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';
        
        // Numeric comparison for balance
        if (sortConfig.key === 'balance') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        // String comparison
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return filtered;
  }, [accounts, search, typeFilter, sortConfig]);
  
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" />
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  const getTypeStyle = (type) => {
    const found = accountTypes.find(t => t.value === type);
    return found?.color || '';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="flex gap-4">
          <Skeleton className="h-10 flex-1 max-w-sm" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="chart-of-accounts">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Chart of Accounts</h1>
          <div className="text-muted-foreground flex items-center gap-2 flex-wrap">
            <span>Your chart of accounts is the foundation of your financial reporting.</span>
            {currentTemplate && templates.length > 0 && (
              <Badge variant="outline" className="text-xs capitalize" data-testid="current-template-badge">
                {templates.find(t => t.id === currentTemplate)?.name || currentTemplate}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => { resetMigration(); setMigrationOpen(true); }}
            data-testid="change-template-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Change Template
          </Button>
          <Button onClick={() => navigate('/accounts/new')} data-testid="add-account-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Account
          </Button>
          <HelpPanel {...coaHelp} />
        </div>
      </div>

      {/* Quick Stats Bar */}
      <div className="flex flex-wrap gap-3">
        <StatsCard icon={BookOpen} label="Total Accounts" value={stats.total} color="blue" />
        <StatsCard icon={Wallet} label="Assets" value={stats.assets} color="blue" />
        <StatsCard icon={Building2} label="Liabilities" value={stats.liabilities} color="rose" />
        <StatsCard icon={PiggyBank} label="Equity" value={stats.equity} color="purple" />
        <StatsCard icon={TrendingUp} label="Income" value={stats.income} color="emerald" />
        <StatsCard icon={TrendingDown} label="Expenses" value={stats.expenses} color="amber" />
      </div>

      {/* Filter Chips */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground mr-2">Filter by type:</span>
        <Button
          variant={typeFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setTypeFilter('all')}
          data-testid="filter-all"
        >
          All
        </Button>
        {accountTypes.map(type => {
          const Icon = type.icon;
          return (
            <Button
              key={type.value}
              variant={typeFilter === type.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTypeFilter(type.value)}
              className="gap-1"
              data-testid={`filter-${type.value}`}
            >
              <Icon className="w-3 h-3" />
              {type.label}
            </Button>
          );
        })}
        
        <div className="ml-auto flex items-center gap-2">
          <Switch 
            id="show-inactive" 
            checked={showInactive}
            onCheckedChange={setShowInactive}
          />
          <Label htmlFor="show-inactive" className="text-sm">Show Inactive</Label>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search accounts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-accounts"
        />
      </div>

      {/* Accounts Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead 
                  className="w-24 cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('code')}
                  data-testid="sort-code"
                >
                  <div className="flex items-center">
                    Code
                    {getSortIcon('code')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('name')}
                  data-testid="sort-account-name"
                >
                  <div className="flex items-center">
                    Account Name
                    {getSortIcon('name')}
                  </div>
                </TableHead>
                <TableHead 
                  className="w-28 cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('type')}
                  data-testid="sort-type"
                >
                  <div className="flex items-center">
                    Type
                    {getSortIcon('type')}
                  </div>
                </TableHead>
                <TableHead className="w-28">Sub-type</TableHead>
                <TableHead 
                  className="text-right w-32 cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('balance')}
                  data-testid="sort-coa-balance"
                >
                  <div className="flex items-center justify-end">
                    Balance
                    {getSortIcon('balance')}
                  </div>
                </TableHead>
                <TableHead className="w-24">Status</TableHead>
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAccounts.length > 0 ? (
                filteredAccounts.map((account) => (
                  <TableRow 
                    key={account.account_id}
                    className={cn(!account.is_active && "opacity-50")}
                  >
                    <TableCell className="font-mono text-sm font-medium">
                      {account.code}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{account.name}</span>
                        {account.is_control && (
                          <Badge variant="outline" className="text-[10px]">
                            Control
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={cn("font-medium", getTypeStyle(account.type))}>
                        {account.type.charAt(0).toUpperCase() + account.type.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {account.control_kind || '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatCurrency(account.balance)}
                    </TableCell>
                    <TableCell>
                      {account.is_active ? (
                        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`account-actions-${account.code}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/accounts/${account.account_id}/edit`)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/ledger?account=${account.account_id}`)}>
                            <FileText className="w-4 h-4 mr-2" />
                            View Ledger
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/accounts/new?parentCode=${encodeURIComponent(account.code)}&type=${account.type}`)}>
                            <Plus className="w-4 h-4 mr-2" />
                            Add Sub-account
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => toggleAccountStatus(account)}>
                            {account.is_active ? (
                              <>
                                <EyeOff className="w-4 h-4 mr-2" />
                                Mark Inactive
                              </>
                            ) : (
                              <>
                                <Eye className="w-4 h-4 mr-2" />
                                Mark Active
                              </>
                            )}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <BookOpen className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                    <p className="text-muted-foreground">No accounts found</p>
                    {search && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Try adjusting your search or filters
                      </p>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* COA Migration Dialog */}
      <Dialog open={migrationOpen} onOpenChange={(open) => { setMigrationOpen(open); if (!open) resetMigration(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {migrationStep === 'select' && 'Change Industry Template'}
              {migrationStep === 'preview' && 'Review Migration'}
            </DialogTitle>
            <DialogDescription>
              {migrationStep === 'select' && 'Select a new industry template for your chart of accounts. Your existing accounts will be mapped to the new template.'}
              {migrationStep === 'preview' && `Migrating to ${migrationPreview?.template_name}. Review the changes below before applying.`}
            </DialogDescription>
          </DialogHeader>

          {/* Step 1: Select Template */}
          {migrationStep === 'select' && (
            <div className="space-y-4" data-testid="migration-step-select">
              <div className="space-y-2">
                <Label>Current Template</Label>
                <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/30">
                  <Badge variant="outline" className="capitalize">{templates.find(t => t.id === currentTemplate)?.name || currentTemplate}</Badge>
                </div>
              </div>
              <div className="space-y-2">
                <Label>New Template</Label>
                <div className="grid gap-2 max-h-[40vh] overflow-y-auto pr-1">
                  {templates.filter(t => t.id !== currentTemplate).map(t => (
                    <button
                      key={t.id}
                      onClick={() => setSelectedTemplate(t.id)}
                      className={cn(
                        "flex items-start gap-3 p-3 rounded-lg border text-left transition-all",
                        selectedTemplate === t.id
                          ? "border-primary bg-primary/5 ring-1 ring-primary"
                          : "border-border hover:border-muted-foreground/30"
                      )}
                      data-testid={`template-option-${t.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{t.name}</span>
                          <Badge variant="secondary" className="text-[10px]">{t.account_count} accounts</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>
                      </div>
                      {selectedTemplate === t.id && (
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setMigrationOpen(false)}>Cancel</Button>
                <Button 
                  onClick={handlePreviewMigration} 
                  disabled={!selectedTemplate || migrationLoading}
                  data-testid="preview-migration-btn"
                >
                  {migrationLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                  Preview Changes
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 2: Preview & Map */}
          {migrationStep === 'preview' && migrationPreview && (
            <div className="space-y-4" data-testid="migration-step-preview">
              {/* Summary stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg border bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 text-center">
                  <p className="text-lg font-bold text-emerald-700 dark:text-emerald-400">{migrationPreview.auto_mapped.length}</p>
                  <p className="text-xs text-emerald-600 dark:text-emerald-500">Auto-mapped</p>
                </div>
                <div className="p-3 rounded-lg border bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-center">
                  <p className="text-lg font-bold text-amber-700 dark:text-amber-400">{migrationPreview.unmapped.length}</p>
                  <p className="text-xs text-amber-600 dark:text-amber-500">Need Mapping</p>
                </div>
                <div className="p-3 rounded-lg border bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 text-center">
                  <p className="text-lg font-bold text-blue-700 dark:text-blue-400">{migrationPreview.new_accounts.length}</p>
                  <p className="text-xs text-blue-600 dark:text-blue-500">New Accounts</p>
                </div>
              </div>

              {/* Auto-mapped accounts */}
              {migrationPreview.auto_mapped.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    <h4 className="text-sm font-medium">Auto-Mapped Accounts ({migrationPreview.auto_mapped.length})</h4>
                  </div>
                  <div className="rounded-lg border overflow-hidden max-h-40 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead className="text-xs py-2">Code</TableHead>
                          <TableHead className="text-xs py-2">Current Name</TableHead>
                          <TableHead className="text-xs py-2">New Name</TableHead>
                          <TableHead className="text-xs py-2 text-right">Entries</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {migrationPreview.auto_mapped.map(a => (
                          <TableRow key={a.account_id} className="text-xs">
                            <TableCell className="py-1.5 font-mono">{a.current_code}</TableCell>
                            <TableCell className="py-1.5">{a.current_name}</TableCell>
                            <TableCell className="py-1.5">{a.new_name}</TableCell>
                            <TableCell className="py-1.5 text-right">{a.entry_count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Unmapped accounts - need user action */}
              {migrationPreview.unmapped.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    <h4 className="text-sm font-medium">Accounts Needing Mapping ({migrationPreview.unmapped.length})</h4>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    These accounts don't exist in the new template. Map them to a new account or leave blank to deactivate.
                  </p>
                  <div className="rounded-lg border overflow-hidden max-h-60 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead className="text-xs py-2">Code</TableHead>
                          <TableHead className="text-xs py-2">Current Name</TableHead>
                          <TableHead className="text-xs py-2 text-right">Entries</TableHead>
                          <TableHead className="text-xs py-2">Map To</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {migrationPreview.unmapped.map(u => (
                          <TableRow key={u.account_id} className="text-xs">
                            <TableCell className="py-1.5 font-mono">{u.current_code}</TableCell>
                            <TableCell className="py-1.5">
                              <div className="flex items-center gap-1">
                                {u.current_name}
                                {u.has_entries && (
                                  <Badge variant="secondary" className="text-[9px] h-4">has txns</Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="py-1.5 text-right">{u.entry_count}</TableCell>
                            <TableCell className="py-1.5">
                              <Select
                                value={accountMappings[u.account_id] || ''}
                                onValueChange={(v) => setAccountMappings(prev => ({ ...prev, [u.account_id]: v }))}
                              >
                                <SelectTrigger className="h-7 text-xs w-44" data-testid={`mapping-select-${u.account_id}`}>
                                  <SelectValue placeholder="Deactivate" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__">
                                    <span className="text-muted-foreground">Deactivate (no mapping)</span>
                                  </SelectItem>
                                  {migrationPreview.new_accounts
                                    .filter(na => na.type === u.type || !u.type)
                                    .map(na => (
                                      <SelectItem key={na.code} value={na.code}>
                                        {na.code} - {na.name}
                                      </SelectItem>
                                    ))}
                                  {/* Also show auto-mapped accounts of same type */}
                                  {migrationPreview.auto_mapped
                                    .filter(am => {
                                      const matchingExisting = accounts.find(a => a.account_id === am.account_id);
                                      return matchingExisting && matchingExisting.type === u.type;
                                    })
                                    .map(am => (
                                      <SelectItem key={am.new_code} value={am.new_code}>
                                        {am.new_code} - {am.new_name}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* New accounts that will be created */}
              {migrationPreview.new_accounts.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Plus className="w-4 h-4 text-blue-500" />
                    <h4 className="text-sm font-medium">New Accounts to Create ({migrationPreview.new_accounts.length})</h4>
                  </div>
                  <div className="rounded-lg border overflow-hidden max-h-32 overflow-y-auto">
                    <div className="flex flex-wrap gap-1.5 p-3">
                      {migrationPreview.new_accounts.map(na => (
                        <Badge key={na.code} variant="outline" className="text-xs">
                          {na.code} - {na.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setMigrationStep('select')}>Back</Button>
                <Button 
                  onClick={handleApplyMigration} 
                  disabled={migrationLoading}
                  data-testid="apply-migration-btn"
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {migrationLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Apply Migration
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
